import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as api from '../../api/educentialApi';

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    fetchStudent();
  }, [id]);

  const fetchStudent = async () => {
    try {
      setLoading(true);
      const response = await api.getStudentDetail(parseInt(id));
      setStudent(response.data);
    } catch (err) {
      console.error('Error fetching student:', err);
      setError(err.message || 'Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      setAddingNote(true);
      await api.addStudentNote(parseInt(id), newNote);
      setNewNote('');
      await fetchStudent(); // Refresh student data
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading student details...</p>
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error || 'Student not found'}</p>
            <button
              onClick={() => navigate('/educator/students')}
              className="mt-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
            >
              Back to Students
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/educator/students"
            className="text-teal-600 hover:text-teal-700 mb-4 inline-block"
          >
            ← Back to Students
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{student.fullName}</h1>
              <p className="text-gray-600 mt-1">{student.email}</p>
            </div>
            <StatusBadge status={student.status} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Student Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Student Information</h2>
              <div className="space-y-3">
                <InfoRow label="Email" value={student.email} />
                <InfoRow label="Phone" value={student.phone || 'Not provided'} />
                <InfoRow label="Country" value={student.country || 'Not provided'} />
                <InfoRow label="City" value={student.city || 'Not provided'} />
                <InfoRow label="Occupation" value={student.occupation || 'Not provided'} />
                <InfoRow
                  label="Assigned Since"
                  value={new Date(student.assignedAt).toLocaleDateString()}
                />
                <InfoRow
                  label="Last Activity"
                  value={
                    student.lastActivity
                      ? new Date(student.lastActivity).toLocaleDateString()
                      : 'Never'
                  }
                />
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
              <div className="space-y-3">
                <StatRow label="Applications" value={student.applicationCount} color="blue" />
                <StatRow label="Documents" value={student.documents.length} color="green" />
                <StatRow label="Services Enrolled" value={student.services.length} color="purple" />
              </div>
            </div>
          </div>

          {/* Right Column - Applications, Services, Notes */}
          <div className="lg:col-span-2 space-y-6">
            {/* Applications */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Applications</h2>
              {student.applications.length > 0 ? (
                <div className="space-y-3">
                  {student.applications.map((app) => (
                    <div key={app.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">
                            {app.service.toUpperCase()} - {app.country}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Created: {new Date(app.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <ApplicationStatusBadge status={app.status} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No applications yet</p>
              )}
            </div>

            {/* Services Enrolled */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Enrolled Services</h2>
              {student.services.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {student.services.map((service, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <p className="font-medium text-gray-900">
                        {service.service.charAt(0).toUpperCase() + service.service.slice(1)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Enrolled: {new Date(service.enrolledAt).toLocaleDateString()}
                      </p>
                      <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${
                        service.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {service.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No services enrolled</p>
              )}
            </div>

            {/* Documents */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
              {student.documents.length > 0 ? (
                <div className="space-y-2">
                  {student.documents.map((doc) => (
                    <div key={doc.id} className="flex justify-between items-center border-b border-gray-200 py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                        <p className="text-xs text-gray-600">{doc.documentType}</p>
                      </div>
                      <DocumentStatusBadge status={doc.status} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No documents uploaded</p>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Educator Notes</h2>
              
              {/* Add Note Form */}
              <form onSubmit={handleAddNote} className="mb-4">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this student..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={addingNote || !newNote.trim()}
                  className="mt-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {addingNote ? 'Adding...' : 'Add Note'}
                </button>
              </form>

              {/* Existing Notes */}
              {student.notes ? (
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-sm text-gray-700">
                  {student.notes}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No notes yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-sm font-medium text-gray-600">{label}:</span>
    <span className="text-sm text-gray-900">{value}</span>
  </div>
);

const StatRow = ({ label, value, color }) => {
  const colors = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
  };

  return (
    <div className="flex justify-between">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-lg font-bold ${colors[color]}`}>{value}</span>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-4 py-2 rounded-full text-sm font-medium ${colors[status]}`}>
      {status}
    </span>
  );
};

const ApplicationStatusBadge = ({ status }) => {
  const colors = {
    DRAFT: 'bg-gray-100 text-gray-800',
    SUBMITTED: 'bg-blue-100 text-blue-800',
    UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
    OFFER_SECURED: 'bg-green-100 text-green-800',
    COMPLETED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const DocumentStatusBadge = ({ status }) => {
  const colors = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status]}`}>
      {status}
    </span>
  );
};

export default StudentDetail;
